/**
 * Is a blur-engine task still going? (VE-30516)
 *
 * One question, asked from the folder row, the dashboard tile and the tile's
 * `redactFile` latch, so it has one answer here rather than a predicate per
 * caller.
 *
 * **One list, and callers complement it.** The dashboard latch used to ask
 * `status !== 'complete'`, which read `failed`, `cancelled` and `aborted` as
 * in-flight: a file whose blur job only ever failed was pinned on "Redacting…"
 * (and on the editor's "Processing redacted version N" banner) for good, since
 * the branch that releases the flag fired only on `complete`. The bug there was
 * that the TERMINAL set was too narrow — not that complementing was wrong.
 *
 * A first attempt at this module replaced that one complement with two
 * independent lists, in-flight and terminal. Their union covered 7 of the 12
 * `TaskStatus` values, so `accepted`, `standby_pending`, `waiting`, `resuming`
 * and `paused` fell into a gap where the latch could neither set nor release —
 * re-entering the very defect this module exists to prevent, silently. Hence a
 * single list: whatever is not in flight is, by construction, something the flag
 * may be released on.
 *
 * **Why release on an unknown status rather than hold.** The two errors are not
 * symmetric. Releasing wrongly shows "not redacting" while a job runs, and the
 * tile then falls back to the raw engine status (`mainPage/selectors.ts`), so
 * the truth is still on screen. Holding wrongly pins a spinner that overrides
 * that fallback and nothing can clear for the life of the tab. The recoverable
 * error is the one to prefer.
 *
 * Every non-terminal `TaskStatus` is listed below, so a status only falls
 * through if the platform adds one. When it does, add it here.
 */

/**
 * Every `TaskStatus` that means the engine has not finished with this task —
 * queued behind others, accepted by a host, parked, or actually executing.
 * Mirrors the non-terminal half of `TaskStatus`
 * (`src/types/veritone-types/index.d.ts`); the terminal half is `complete`,
 * `failed`, `cancelled` and `aborted`.
 */
const IN_FLIGHT_STATUSES: ReadonlyArray<string> = [
  'queued',
  'pending',
  'running',
  'accepted',
  'waiting',
  'standby_pending',
  'resuming',
  'paused',
];

/**
 * `string | null | undefined`, not `unknown`: every caller has a typed task
 * status to hand, and a wider signature would silently answer `false` for a
 * whole `Task` passed by mistake instead of failing to compile.
 */
type MaybeStatus = string | null | undefined;

const normalize = (status: MaybeStatus): string =>
  typeof status === 'string' ? status.toLowerCase() : '';

/**
 * The engine still has work to do on this task.
 *
 * Callers that need "the task is over" complement this rather than asking a
 * second list, so the two answers cannot disagree. Guard the complement on a
 * task actually existing: `isRedactTaskInFlight(undefined)` is `false`, and a
 * file with no blur task at all is not a finished burn-in — it is an image or
 * document burn-in that runs in the browser and creates no engine task
 * (VE-28061).
 */
export const isRedactTaskInFlight = (status: MaybeStatus): boolean =>
  IN_FLIGHT_STATUSES.includes(normalize(status));

/** It finished, and it produced an output. */
export const isRedactTaskComplete = (status: MaybeStatus): boolean =>
  normalize(status) === 'complete';
